import { CSSObject } from '@styled-system/css';
import { ColorKeys, palette } from './colors';
import { SolvSystemProps } from './system';
import { Variant } from './systemTypes';
import { typography } from './typography';

export type ButtonVariant = 'contained' | 'outlined' | 'text';
export type ButtonTone = 'primary' | 'error' | 'warning' | 'success';

/**
 * Given an object of colors returns primary, error, success, and warning tones
 * @param colors object mapping color names to hex values, from SolvTheme object
 * @returns an object of 4 different tones and their base, dark, light, xlight properties
 */
export const getTone = (colors: { [key in ColorKeys]: string }) => {
  return {
    primary: {
      '--button-base': colors.crunchberry,
      '--button-dark': colors['crunchberry-dark'],
      '--button-light': colors['crunchberry-medium'],
      '--button-xlight': colors['crunchberry-xlight'],
    },
    error: {
      '--button-base': palette.cherry.base,
      '--button-dark': palette.cherry.base,
      '--button-light': palette.cherry.dark,
      '--button-xlight': palette.cherry.light,
    },
    success: {
      '--button-base': palette.mint.base,
      '--button-dark': palette.mint.base,
      '--button-light': palette.mint.dark,
      '--button-xlight': palette.mint.light,
    },
    warning: {
      '--button-base': palette.peach.base,
      '--button-dark': palette.peach.base,
      '--button-light': palette.peach.dark,
      '--button-xlight': palette.peach.light,
    },
  };
};

export const buttons: Variant<ButtonVariant> = {
  contained: {
    background: 'var(--button-base)',
    color: 'white',
    border: 'solid 1px var(--button-base)',
    ':visited': {
      color: 'white',
    },
    ':hover': {
      background: 'var(--button-light)',
      border: 'solid 1px var(--button-light)',
      color: 'white',
    },
    ':active': {
      background: 'var(--button-dark)',
      border: 'solid 1px var(--button-dark)',
      color: 'white',
    },
    ':disabled,.disabled': {
      pointerEvents: 'none',
      bg: 'gray-200',
      color: 'gray-300',
      borderColor: 'gray-200',
    },
  },
  outlined: {
    background: 'transparent',
    color: 'var(--button-base)',
    border: 'solid 1px var(--button-base)',
    ':visited': {
      color: 'var(--button-base)',
    },
    ':hover': {
      background: 'var(--button-xlight)',
      color: 'var(--button-base)',
    },
    ':active': {
      background: 'transparent',
      color: 'var(--button-base)',
    },
    ':disabled,.disabled': {
      bg: 'white',
      borderColor: 'gray-300',
      color: 'gray-300',
    },
  },
  text: {
    ...typography.body,
    background: 'transparent',
    color: 'var(--button-base)',
    border: 'solid 1px transparent',
    ':visited': {
      color: 'var(--button-base)',
    },
    ':hover': {
      background: 'var(--button-xlight)',
      color: 'var(--button-base)',
    },
    ':active': {
      background: 'transparent',
      color: 'var(--button-base)',
    },
    ':disabled,.disabled': {
      bg: 'white',
      borderColor: 'gray-300',
      color: 'gray-300',
    },
  },
};

export type ButtonSize = 'large' | 'medium' | 'small';

export const buttonSizes: Record<ButtonSize, SolvSystemProps & CSSObject> = {
  large: {
    px: 3,
    py: 2,
  },
  medium: {
    px: 2.5,
    py: 1.5,
  },
  small: {
    px: 2,
    py: 1,
  },
};
